import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { FiArrowLeft, FiGift, FiPlus, FiSave, FiTrash2, FiZap } from 'react-icons/fi';
import './PowerHourPage.css';

interface ScheduleBlock { name: string; days: number[]; start: string; end: string; multiplier: number; }
interface Window_ { name: string; days: number[]; start: string; end: string; multiplier: number; enabled: boolean; }
interface Milestone { meetings: number; reward: string; }
interface BonusRule { name: string; metric: string; count: number; period: string; reward: string; enabled: boolean; auto_approve?: boolean; }
interface SlackChannel { channel: string; power_hours: boolean; boosts: boolean; winners?: boolean; celebrations?: boolean; }
interface Payout { id: number; period: string; period_key: string; track: string; winner_name: string; winner_email: string; amount: number; prize_label: string; status: string; }
interface MarketItem { key: string; code?: string; label: string; coins: number; teams: string[]; requires_approval: boolean; poolable: boolean; gift_card_amount: number; stock_per_month: number; per_rep_per_month: number; enabled: boolean; eligible_days?: number[]; blackout_power_hours?: boolean; }
interface EconTeam {
  reps: number; weeks_sampled: number; median_coins_week: number;
  mean_coins_week: number; slowest_rep: string; slowest_coins_week: number;
}
interface EconItem {
  key: string; label: string; coins: number; team: string; enabled: boolean;
  poolable: boolean; weeks_median: number | null; weeks_slowest: number | null;
  pct_of_season: number | null; reachable_by_median: boolean;
}
interface Economy {
  season: string; season_weeks: number; weeks_left: number; weeks_sampled: number;
  teams: Record<string, EconTeam>; catalog: EconItem[];
  minted_season: number; redeemed_season: number; overhang: number;
  by_item: { key: string; label: string; count: number; coins: number }[];
  unmapped_reps: string[];
}
interface Redemption { id: number; rep_name: string; rep_email: string; kind: string; item_key: string; item_label: string; coins: number; gift_card_amount: number; season_key: string; requested_date?: string | null; status: string; created_at: string; }
interface PHConfig {
  meeting_target: number;
  schedule: ScheduleBlock[];
  windows: Window_[];
  prizes: { individual: Record<string, string> };
  milestones: { period: string; items: Milestone[] };
  teams: Record<string, string[]>;
  team_battle_prize: string;
  excluded_reps: string[];
  streaks: { nudges: boolean; nudge_hour: number; reward_days: number; reward: string };
  bonus_rules?: BonusRule[];
  coin_values?: { meeting: number };
  wheel?: {
    enabled: boolean; per_rep_per_week: number;
    prices: Record<string, number>;
    outcomes: { key: string; label: string; weight: number; coins_pct?: number;
      fuel_pct_of_boost?: number; boost_minutes?: number; respin?: boolean }[];
  };
  boosts?: {
    enabled: boolean; multiplier: number; per_rep_per_week: number;
    fuel_per_dial: number; fuel_per_connect: number;
    durations_minutes: Record<string, number>;
    prices: Record<string, Record<string, number>>;
  };
  marketplace?: { enabled: boolean; award_coins_max: number; nudges: boolean; pilot_reps: string[]; catalog: MarketItem[] };
  slack: { announce: boolean; remind_minutes: number; channels: SlackChannel[] };
}

const DAY_LABELS = ['M', 'T', 'W', 'Th', 'F', 'Sa', 'Su'];

const PowerHourConfig: React.FC = () => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState<PHConfig | null>(null);
  const [reps, setReps] = useState<string[]>([]);
  const [slackChannels, setSlackChannels] = useState<{ id: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [saveError, setSaveError] = useState('');
  const [newTeam, setNewTeam] = useState('');
  const [pin, setPin] = useState(() => sessionStorage.getItem('ph-config-pin') || '');
  const [pinOk, setPinOk] = useState(false);
  const [pinError, setPinError] = useState('');
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [fulfillmentReady, setFulfillmentReady] = useState(true);
  // a Tremendous send takes 5–10s — track which row is in flight so the queue
  // can show progress and refuse a second click while money is moving
  const [busyPayoutId, setBusyPayoutId] = useState<number | null>(null);
  const [busyKind, setBusyKind] = useState<'send' | 'skip'>('send');
  const [payoutMsg, setPayoutMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // one-off bonus coin award
  const [awardName, setAwardName] = useState('');
  const [awardCoins, setAwardCoins] = useState('');
  const [awardNote, setAwardNote] = useState('');
  const [awarding, setAwarding] = useState(false);
  const [awardMsg, setAwardMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // manual gift card (real money — sends immediately)
  const [giftName, setGiftName] = useState('');
  const [giftEmail, setGiftEmail] = useState('');
  const [giftAmount, setGiftAmount] = useState('');
  const [giftNote, setGiftNote] = useState('');
  const [gifting, setGifting] = useState(false);
  const [giftMsg, setGiftMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const tryPin = async (p: string) => {
    const res = await fetch(`${API_BASE_URL}/api/power-hour/config/verify-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin: p }),
    });
    if (res.ok) {
      sessionStorage.setItem('ph-config-pin', p);
      setPin(p);
      setPinOk(true);
      setPinError('');
    } else {
      setPinError('Wrong PIN — ask the sales leader.');
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem('ph-config-pin');
    if (stored) tryPin(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/power-hour/config`).then(r => r.json()).then(d => setCfg(d.config));
    fetch(`${API_BASE_URL}/api/power-hour/reps?active=1`).then(r => r.json()).then(d => setReps(d.reps || [])).catch(() => {});
    fetch(`${API_BASE_URL}/api/power-hour/slack-channels`).then(r => r.json()).then(d => setSlackChannels(d.channels || [])).catch(() => {});
  }, []);

  const visibleReps = reps.filter(r => !(cfg?.excluded_reps || []).includes(r));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/power-hour/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Config-Pin': pin },
        body: JSON.stringify(cfg),
      });
      if (res.status === 403) {
        sessionStorage.removeItem('ph-config-pin');
        setPinOk(false);
        return;
      }
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(d.error || `Save failed (${res.status}) — nothing was changed.`);
        return;
      }
      setSaveError('');
      if (d.config) setCfg(d.config);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  // marketplace redemptions — backup queue (Kyle normally decides from Slack)
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [busyRedemptionId, setBusyRedemptionId] = useState<number | null>(null);
  const [redemptionMsg, setRedemptionMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const loadRedemptions = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/power-hour/redemptions?status=pending`);
      const d = await r.json();
      setRedemptions(d.redemptions || []);
    } catch { /* table may not exist yet */ }
  };
  useEffect(() => { if (pinOk) loadRedemptions(); }, [pinOk]);

  const decideRedemption = async (id: number, approve: boolean) => {
    if (busyRedemptionId !== null) return; // one decision at a time
    const rr = redemptions.find(x => x.id === id);
    setBusyRedemptionId(id);
    setRedemptionMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/power-hour/redemptions/${id}/${approve ? 'approve' : 'deny'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Config-Pin': pin },
        body: JSON.stringify({}),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) {
        setRedemptionMsg({ ok: false, text: d.error || 'Decision failed.' });
        return;
      }
      setRedemptionMsg({
        ok: true,
        text: approve
          ? `✅ Approved ${rr?.item_label ?? 'the redemption'} for ${rr?.rep_name ?? 'the rep'} — they got a DM.`
          : `❌ Denied — ${(rr?.coins ?? 0).toLocaleString()} 🪙 returned to ${rr?.rep_name ?? 'the rep'}.`,
      });
      await loadRedemptions();
    } catch {
      setRedemptionMsg({ ok: false, text: 'Network error — re-check the queue before retrying.' });
    } finally {
      setBusyRedemptionId(null);
    }
  };

  // economy health — is the catalog priced for what reps actually earn?
  const [econ, setEcon] = useState<Economy | null>(null);

  const loadEconomy = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/power-hour/economy`, {
        headers: { 'X-Config-Pin': pin },
      });
      if (r.ok) setEcon(await r.json());
    } catch { /* endpoint not deployed yet */ }
  };
  // pin is set before pinOk flips, so gating on pinOk alone is correct here —
  // re-running on every pin keystroke would just fire 403s
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (pinOk) loadEconomy(); }, [pinOk]);

  const loadPayouts = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/power-hour/payouts?status=pending`);
      const d = await r.json();
      setPayouts(d.payouts || []);
      setFulfillmentReady(d.fulfillment_ready !== false);
    } catch { /* table may not exist yet */ }
  };
  useEffect(() => { if (pinOk) loadPayouts(); }, [pinOk]);

  const setPayoutEmail = (id: number, email: string) =>
    setPayouts(ps => ps.map(p => (p.id === id ? { ...p, winner_email: email } : p)));

  const approvePayout = async (id: number, email: string) => {
    if (busyPayoutId !== null) return;  // one send at a time — this moves money
    const p = payouts.find(x => x.id === id);
    setBusyPayoutId(id);
    setBusyKind('send');
    setPayoutMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/power-hour/payouts/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Config-Pin': pin },
        body: JSON.stringify({ email }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setPayoutMsg({ ok: false, text: d.error || 'Payout failed to send.' });
        return;
      }
      // the row leaves the pending queue on refresh, so the confirmation has to
      // live on the card — otherwise the send just looks like a row vanishing
      setPayoutMsg({
        ok: true,
        text: `💸 $${p?.amount ?? ''} gift card sent to ${p?.winner_name ?? 'the winner'} (${email}) — reward link emailed.`,
      });
      await loadPayouts();
    } catch {
      setPayoutMsg({ ok: false, text: 'Network error — the payout may not have sent. Re-check the queue before retrying.' });
    } finally {
      setBusyPayoutId(null);
    }
  };

  const skipPayout = async (id: number) => {
    if (busyPayoutId !== null) return;
    const p = payouts.find(x => x.id === id);
    setBusyPayoutId(id);
    setBusyKind('skip');
    setPayoutMsg(null);
    try {
      await fetch(`${API_BASE_URL}/api/power-hour/payouts/${id}/skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Config-Pin': pin },
        body: JSON.stringify({}),
      });
      setPayoutMsg({ ok: true, text: `Skipped ${p?.winner_name ?? 'that payout'} — no gift card sent.` });
      await loadPayouts();
    } catch {
      setPayoutMsg({ ok: false, text: 'Network error — could not skip that payout.' });
    } finally {
      setBusyPayoutId(null);
    }
  };

  const awardBonus = async () => {
    const name = awardName.trim();
    const coins = parseInt(awardCoins, 10);
    if (!name || !coins || coins < 1) {
      setAwardMsg({ ok: false, text: 'Enter a name and a coin amount.' });
      return;
    }
    setAwarding(true);
    setAwardMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/power-hour/award-coins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Config-Pin': pin },
        body: JSON.stringify({ name, coins, note: awardNote.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAwardMsg({ ok: false, text: d.error || 'Award failed.' });
        return;
      }
      setAwardMsg({ ok: true, text: `🎰 ${coins.toLocaleString()} 🪙 awarded to ${name} — posted to Slack + jackpot on the TV.` });
      setAwardName('');
      setAwardCoins('');
      setAwardNote('');
    } finally {
      setAwarding(false);
    }
  };

  const issueGiftCard = async () => {
    const name = giftName.trim();
    const amount = parseFloat(giftAmount);
    if (!name || !amount || amount < 1) {
      setGiftMsg({ ok: false, text: 'Enter a name and a dollar amount.' });
      return;
    }
    if (!window.confirm(`Send a REAL $${amount} gift card to ${name} right now? This moves money.`)) return;
    setGifting(true);
    setGiftMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/power-hour/gift-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Config-Pin': pin },
        body: JSON.stringify({ name, email: giftEmail.trim(), amount, note: giftNote.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setGiftMsg({ ok: false, text: d.error || 'Gift card failed to send.' });
        return;
      }
      setGiftMsg({ ok: true, text: `💳 $${amount} gift card sent to ${name} (${d.gift?.email}) — reward link emailed, JACKPOT posted to Slack + TV.` });
      setGiftName('');
      setGiftEmail('');
      setGiftAmount('');
      setGiftNote('');
      loadPayouts(); // the audit row lands in the payouts table
    } finally {
      setGifting(false);
    }
  };

  if (!pinOk) {
    return (
      <div className="ph-page">
        <div className="ph-topbar">
          <div className="ph-logo">
            <button className="ph-icon-btn" onClick={() => navigate('/power-hour')} title="Back">
              <FiArrowLeft />
            </button>
            <FiZap /> EARN THE SUMMIT — CONFIG
          </div>
        </div>
        <div className="ph-setup">
          <div className="ph-setup-card">
            <h2>🔒 ENTER PIN</h2>
            <p className="ph-setup-sub">Config changes the game for everyone — leaders only.</p>
            <input
              className="ph-join-input"
              type="password"
              placeholder="• • •"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') tryPin((e.target as HTMLInputElement).value);
              }}
            />
            {pinError && <div className="ph-join-error" style={{ marginTop: 10 }}>{pinError}</div>}
            <button className="ph-cta" style={{ marginTop: 14 }}
              onClick={e => {
                const input = (e.currentTarget.parentElement?.querySelector('input') as HTMLInputElement);
                tryPin(input?.value || '');
              }}>UNLOCK</button>
          </div>
        </div>
      </div>
    );
  }

  if (!cfg) return <div className="ph-page"><div className="ph-empty">Loading config…</div></div>;

  const upd = (patch: Partial<PHConfig>) => setCfg({ ...cfg, ...patch });
  const toggleDay = (days: number[], d: number) =>
    days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort();

  // ---- marketplace helpers ----
  const mp = cfg.marketplace || { enabled: false, award_coins_max: 10000, nudges: true, pilot_reps: [], catalog: [] };
  const updMp = (patch: Partial<NonNullable<PHConfig['marketplace']>>) =>
    upd({ marketplace: { ...mp, ...patch } });
  const updItem = (i: number, patch: Partial<MarketItem>) =>
    updMp({ catalog: mp.catalog.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  // the key is the item's stable identity (redemption rows reference it, like
  // _rule_track for prize rules) — slugged once from the label, then read-only
  const slugKey = (label: string) => {
    const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'item';
    const taken = new Set(mp.catalog.map(x => x.key));
    let key = base;
    for (let n = 2; taken.has(key); n++) key = `${base}_${n}`;
    return key;
  };

  const teamOf = (rep: string) =>
    Object.keys(cfg.teams).find(t => cfg.teams[t].includes(rep)) || '';
  const setTeam = (rep: string, team: string) => {
    const teams: Record<string, string[]> = {};
    for (const t of Object.keys(cfg.teams)) teams[t] = cfg.teams[t].filter(r => r !== rep);
    if (team) teams[team] = [...(teams[team] || []), rep];
    upd({ teams });
  };
  const addTeam = () => {
    const name = newTeam.trim();
    if (!name || cfg.teams[name]) return;
    upd({ teams: { ...cfg.teams, [name]: [] } });
    setNewTeam('');
  };
  const removeTeam = (name: string) => {
    const teams = { ...cfg.teams };
    delete teams[name];
    upd({ teams });
  };

  return (
    <div className="ph-page ph-config">
      <div className="ph-topbar">
        <div className="ph-logo">
          <button className="ph-icon-btn" onClick={() => navigate('/power-hour')} title="Back">
            <FiArrowLeft />
          </button>
          <FiZap /> EARN THE SUMMIT — CONFIG
        </div>
        <button className="ph-cta ph-cta-narrow" onClick={save} disabled={saving}>
          <FiSave style={{ verticalAlign: '-2px' }} /> {saving ? 'SAVING…' : savedAt && Date.now() - savedAt < 2500 ? 'SAVED ✓' : 'SAVE'}
        </button>
      </div>
      {saveError && <div className="ph-join-error" style={{ margin: '0 0 12px' }}>⚠️ Not saved: {saveError}</div>}

      <div className="ph-cfg-grid">
        {/* ---- Scheduled power hours ---- */}
        <div className="ph-cfg-card">
          <h3>⚡ Scheduled Power Hours <span className="ph-cfg-sub">multiplies ALL coins · Slack hype + final summary · takes over the TV</span></h3>
          {cfg.schedule.map((b, i) => (
            <div key={i} className="ph-cfg-block">
              <div className="ph-cfg-row">
                <input className="ph-cfg-name" value={b.name}
                  onChange={e => upd({ schedule: cfg.schedule.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} />
                <button className="ph-icon-btn" onClick={() => upd({ schedule: cfg.schedule.filter((_, j) => j !== i) })}><FiTrash2 /></button>
              </div>
              <div className="ph-cfg-row">
                <span className="ph-cfg-days">
                  {DAY_LABELS.map((lbl, d) => (
                    <button key={d} className={`ph-day-btn ${b.days.includes(d) ? 'on' : ''}`}
                      onClick={() => upd({ schedule: cfg.schedule.map((x, j) => j === i ? { ...x, days: toggleDay(x.days, d) } : x) })}>{lbl}</button>
                  ))}
                </span>
              </div>
              <div className="ph-cfg-row">
                <label>start <input type="time" value={b.start}
                  onChange={e => upd({ schedule: cfg.schedule.map((x, j) => j === i ? { ...x, start: e.target.value } : x) })} /></label>
                <label>end <input type="time" value={b.end}
                  onChange={e => upd({ schedule: cfg.schedule.map((x, j) => j === i ? { ...x, end: e.target.value } : x) })} /></label>
                <label>mult <input type="number" min={1} step={0.5} value={b.multiplier}
                  onChange={e => upd({ schedule: cfg.schedule.map((x, j) => j === i ? { ...x, multiplier: parseFloat(e.target.value) || 2 } : x) })} /></label>
              </div>
            </div>
          ))}
          <button className="ph-mini-cta" onClick={() => upd({
            schedule: [...cfg.schedule, { name: '⚡ POWER HOUR', days: [1, 3], start: '08:00', end: '11:30', multiplier: 2 }],
          })}><FiPlus /> Add block</button>
        </div>

        {/* ---- THE meeting target ---- */}
        <div className="ph-cfg-card">
          <h3>🎯 Meeting Target <span className="ph-cfg-sub">the one number — power hours and the daily hill aim for it; week = ×5, month = ×20</span></h3>
          <div className="ph-cfg-row">
            <input type="number" min={1} className="ph-cfg-num" value={cfg.meeting_target}
              onChange={e => upd({ meeting_target: parseInt(e.target.value) || 10 })} />
            <span className="ph-cfg-sub" style={{ marginTop: 0 }}>
              → day 🎯 {cfg.meeting_target} · week 🎯 {cfg.meeting_target * 5} · month 🎯 {cfg.meeting_target * 20}
            </span>
          </div>
        </div>

        {/* ---- Boost windows ---- */}
        <div className="ph-cfg-card">
          <h3>🚀 Bonus Hours <span className="ph-cfg-sub">scheduled windows that multiply MEETING coins outside a power hour. (Named Bonus Hours, not boosts — a “boost” is the personal multiplier a rep buys with Fuel)</span></h3>
          {cfg.windows.map((w, i) => (
            <div key={i} className="ph-cfg-block">
              <div className="ph-cfg-row">
                <label className="ph-cfg-toggle">
                  <input type="checkbox" checked={w.enabled}
                    onChange={e => upd({ windows: cfg.windows.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x) })} />
                  <input className="ph-cfg-name" value={w.name}
                    onChange={e => upd({ windows: cfg.windows.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} />
                </label>
                <button className="ph-icon-btn" onClick={() => upd({ windows: cfg.windows.filter((_, j) => j !== i) })}><FiTrash2 /></button>
              </div>
              <div className="ph-cfg-row">
                <span className="ph-cfg-days">
                  {DAY_LABELS.map((lbl, d) => (
                    <button key={d} className={`ph-day-btn ${w.days.includes(d) ? 'on' : ''}`}
                      onClick={() => upd({ windows: cfg.windows.map((x, j) => j === i ? { ...x, days: toggleDay(x.days, d) } : x) })}>{lbl}</button>
                  ))}
                </span>
                <label>start <input type="time" value={w.start}
                  onChange={e => upd({ windows: cfg.windows.map((x, j) => j === i ? { ...x, start: e.target.value } : x) })} /></label>
                <label>end <input type="time" value={w.end}
                  onChange={e => upd({ windows: cfg.windows.map((x, j) => j === i ? { ...x, end: e.target.value } : x) })} /></label>
                <label>mult <input type="number" min={1} step={0.5} value={w.multiplier}
                  onChange={e => upd({ windows: cfg.windows.map((x, j) => j === i ? { ...x, multiplier: parseFloat(e.target.value) || 2 } : x) })} /></label>
              </div>
            </div>
          ))}
          <button className="ph-mini-cta" onClick={() => upd({
            windows: [...cfg.windows, { name: '✨ Custom boost', days: [0, 1, 2, 3, 4], start: '12:00', end: '13:00', multiplier: 2, enabled: true }],
          })}><FiPlus /> Add window</button>
        </div>

        {/* ---- Prizes ---- */}
        <div className="ph-cfg-card">
          <h3>🎁 Champion Prizes <span className="ph-cfg-sub">most coins wins the crown — and since meetings are the only coin source, the crown goes to the best booker. Week and month send automatically; the daily prize queues in 💸 Payouts</span></h3>
          {(['day', 'week', 'month'] as const).map(p => (
            <div key={p} className="ph-cfg-row">
              <span className="ph-cfg-period">{p.toUpperCase()}</span>
              <input placeholder={`🏆 Champion prize — e.g. ${p === 'day' ? '$25 DoorDash' : p === 'week' ? '$100 gift card' : '$500 travel voucher'}`}
                value={cfg.prizes.individual[p] || ''}
                onChange={e => upd({ prizes: { ...cfg.prizes, individual: { ...cfg.prizes.individual, [p]: e.target.value } } })} />
            </div>
          ))}
          <div className="ph-cfg-sub">
            🎯 Top Closer (most meetings) still gets a shout-out in Slack, but no longer has a prize of
            its own — it only fires when the closer isn't already the Champion, which happened once in 60 awards.
          </div>
        </div>

        {/* ---- Personal boosts (Fuel economy) ---- */}
        {cfg.boosts && (
          <div className="ph-cfg-card">
            <h3>🚀 Personal Boosts <span className="ph-cfg-sub">a rep spends ⛽ Fuel (minted by dials + connects, resets Monday, never touches coins) on a personal multiplier via /coins boost. Stacks with power hours, hard-capped at 4x. Priced per team — AEs dial ~4x less</span></h3>
            <div className="ph-cfg-row">
              <label className="ph-cfg-toggle">
                <input type="checkbox" checked={cfg.boosts.enabled}
                  onChange={e => upd({ boosts: { ...cfg.boosts!, enabled: e.target.checked } })} />
                boosts on
              </label>
              <label>mult <input type="number" min={1} max={4} step={0.5} className="ph-cfg-num" value={cfg.boosts.multiplier}
                onChange={e => upd({ boosts: { ...cfg.boosts!, multiplier: parseFloat(e.target.value) || 2 } })} /></label>
              <label>max/week <input type="number" min={1} className="ph-cfg-num" value={cfg.boosts.per_rep_per_week}
                onChange={e => upd({ boosts: { ...cfg.boosts!, per_rep_per_week: parseInt(e.target.value) || 3 } })} /></label>
              <label>⛽/dial <input type="number" min={0} className="ph-cfg-num" value={cfg.boosts.fuel_per_dial}
                onChange={e => upd({ boosts: { ...cfg.boosts!, fuel_per_dial: parseInt(e.target.value) || 0 } })} /></label>
              <label>⛽/connect <input type="number" min={0} className="ph-cfg-num" value={cfg.boosts.fuel_per_connect}
                onChange={e => upd({ boosts: { ...cfg.boosts!, fuel_per_connect: parseInt(e.target.value) || 0 } })} /></label>
            </div>
            {Object.keys(cfg.teams).map(t => (
              <div key={t} className="ph-cfg-row">
                <span className="ph-cfg-period">{t}</span>
                {Object.keys(cfg.boosts!.durations_minutes || { '1h': 60, '2h': 120 }).sort().map(d => (
                  <label key={d}>{d} <input type="number" min={1} className="ph-cfg-num"
                    value={cfg.boosts!.prices?.[t]?.[d] ?? 0}
                    onChange={e => upd({
                      boosts: {
                        ...cfg.boosts!,
                        prices: {
                          ...cfg.boosts!.prices,
                          [t]: { ...(cfg.boosts!.prices?.[t] || {}), [d]: parseInt(e.target.value) || 0 },
                        },
                      },
                    })} /> ⛽</label>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* ---- Mystery wheel ---- */}
        {cfg.wheel && (
          <div className="ph-cfg-card">
            <h3>🎡 Mystery Wheel <span className="ph-cfg-sub">spend coins, win WORK AMMO — Fuel, a boost, a MEGA 3x, or a Fuel Rush for the whole floor. Never coins back (that would be gambling on the perk currency) and never time-off — both enforced: a coin-paying slice makes the wheel refuse to spin. Every roll is written to the ledger</span></h3>
            <div className="ph-cfg-row">
              <label className="ph-cfg-toggle">
                <input type="checkbox" checked={cfg.wheel.enabled}
                  onChange={e => upd({ wheel: { ...cfg.wheel!, enabled: e.target.checked } })} />
                wheel on
              </label>
              <label>spins/week <input type="number" min={1} className="ph-cfg-num" value={cfg.wheel.per_rep_per_week}
                onChange={e => upd({ wheel: { ...cfg.wheel!, per_rep_per_week: parseInt(e.target.value) || 2 } })} /></label>
              {Object.keys(cfg.teams).map(t => (
                <label key={t}>{t} <input type="number" min={100} className="ph-cfg-num" value={cfg.wheel!.prices?.[t] ?? 0}
                  onChange={e => upd({ wheel: { ...cfg.wheel!, prices: { ...cfg.wheel!.prices, [t]: parseInt(e.target.value) || 0 } } })} /> 🪙</label>
              ))}
            </div>
            {(() => {
              // mirror of the server's promise check, so a bad edit is red BEFORE save
              const outs = cfg.wheel!.outcomes || [];
              const total = outs.reduce((a, o) => a + (o.weight || 0), 0) || 1;
              const respin = outs.filter(o => o.respin).reduce((a, o) => a + (o.weight || 0), 0) / total;
              const paysCoins = outs.some(o => (o.coins_pct || 0) > 0);
              const bad = paysCoins || respin > 0.3;
              return (
                <>
                  {outs.map(o => (
                    <div key={o.key} className="ph-cfg-row">
                      <span className="ph-cfg-sub" style={{ marginTop: 0 }}>
                        {Math.round(1000 * (o.weight || 0) / total) / 10}% — {o.label}
                      </span>
                    </div>
                  ))}
                  <div className={`ph-cfg-sub${bad ? ' ph-cfg-warn' : ''}`}>
                    {paysCoins ? '⚠️ a slice pays coins — the wheel will refuse to spin. It pays work ammo only'
                      : respin > 0.3 ? '⚠️ respin weight over 30% — the wheel will refuse to spin'
                      : 'work ammo only — every slice drives dials'} ·
                    outcome table edits via config API only — weights and prizes deserve more ceremony than a number box
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ---- Feat-based prize rules ---- */}
        <div className="ph-cfg-card">
          <h3>🏅 Prize Rules <span className="ph-cfg-sub">automatic prizes for specific feats — e.g. 3 meetings in 1 day = $100 gift card. Fires the moment a rep crosses the line (once per rep per period), celebrates in Slack + TV; dollar rewards queue in 💸 Payouts for approval</span></h3>
          {(cfg.bonus_rules || []).map((r, i) => (
            <div key={i} className="ph-cfg-block">
              <div className="ph-cfg-row">
                <label className="ph-cfg-toggle">
                  <input type="checkbox" checked={r.enabled}
                    onChange={e => upd({ bonus_rules: (cfg.bonus_rules || []).map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x) })} />
                  <input className="ph-cfg-name" placeholder="Rule name — e.g. 🎩 Hat-Trick Bonus" value={r.name}
                    onChange={e => upd({ bonus_rules: (cfg.bonus_rules || []).map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} />
                </label>
                <button className="ph-icon-btn" onClick={() => upd({ bonus_rules: (cfg.bonus_rules || []).filter((_, j) => j !== i) })}><FiTrash2 /></button>
              </div>
              <div className="ph-cfg-row">
                <input type="number" min={1} className="ph-cfg-num" value={r.count}
                  onChange={e => upd({ bonus_rules: (cfg.bonus_rules || []).map((x, j) => j === i ? { ...x, count: parseInt(e.target.value) || 1 } : x) })} />
                <select value={r.metric}
                  onChange={e => upd({ bonus_rules: (cfg.bonus_rules || []).map((x, j) => j === i ? { ...x, metric: e.target.value } : x) })}>
                  <option value="meetings">meetings</option>
                  <option value="connects">connects</option>
                  <option value="dials">dials</option>
                  <option value="coins">coins</option>
                </select>
                <span className="ph-cfg-sub" style={{ marginTop: 0 }}>in 1</span>
                <select value={r.period}
                  onChange={e => upd({ bonus_rules: (cfg.bonus_rules || []).map((x, j) => j === i ? { ...x, period: e.target.value } : x) })}>
                  <option value="day">day</option><option value="week">week</option><option value="month">month</option>
                </select>
                <input placeholder="Reward — e.g. $100 gift card" value={r.reward}
                  onChange={e => upd({ bonus_rules: (cfg.bonus_rules || []).map((x, j) => j === i ? { ...x, reward: e.target.value } : x) })} />
              </div>
              <div className="ph-cfg-row">
                <label className="ph-cfg-toggle">
                  <input type="checkbox" checked={!!r.auto_approve}
                    onChange={e => upd({ bonus_rules: (cfg.bonus_rules || []).map((x, j) => j === i ? { ...x, auto_approve: e.target.checked } : x) })} />
                  pay it automatically
                </label>
                <span className="ph-cfg-sub" style={{ marginTop: 0 }}>
                  the feat is already proven by the numbers — send the gift card the moment it lands,
                  instead of parking it in 💸 Payouts for approval
                </span>
              </div>
            </div>
          ))}
          <button className="ph-mini-cta" onClick={() => upd({
            bonus_rules: [...(cfg.bonus_rules || []), { name: '🎩 Hat-Trick Bonus', metric: 'meetings', count: 3, period: 'day', reward: '$100 gift card', enabled: true, auto_approve: true }],
          })}><FiPlus /> Add rule</button>
        </div>

        {/* ---- The Vault marketplace ---- */}
        <div className="ph-cfg-card">
          <h3>🛍️ The Vault — Marketplace <span className="ph-cfg-sub">reps redeem coins in Slack: /coins balance · shop · redeem · pool. Wallets are quarterly (earned this quarter − spent); career totals and leaderboards never change. Approval-gated items DM the approver with buttons; this page keeps the backup queue</span></h3>
          <div className="ph-cfg-row">
            <label className="ph-cfg-toggle">
              <input type="checkbox" checked={mp.enabled}
                onChange={e => updMp({ enabled: e.target.checked })} />
              Marketplace open
            </label>
            <label className="ph-cfg-toggle">
              <input type="checkbox" checked={mp.nudges}
                onChange={e => updMp({ nudges: e.target.checked })} />
              "You can now afford…" DMs
            </label>
            <label>🎰 max hand-mint <input type="number" min={1} className="ph-cfg-num" value={mp.award_coins_max}
              onChange={e => updMp({ award_coins_max: parseInt(e.target.value) || 10000 })} /></label>
          </div>
          <div className="ph-cfg-row">
            <span className="ph-cfg-sub" style={{ marginTop: 0 }}>🪙 coins per meeting (future calls only):</span>
            {/* merge-patch, never a rebuilt object — a handler that rewrites
                coin_values wholesale silently drops any key it doesn't know */}
            <label>meeting <input type="number" min={0} className="ph-cfg-num" value={cfg.coin_values?.meeting ?? 500}
              onChange={e => upd({ coin_values: { ...(cfg.coin_values || {}), meeting: parseInt(e.target.value) || 0 } })} /></label>
            <span className="ph-cfg-sub" style={{ marginTop: 0 }}>
              connects and dials earn nothing — they're activity, not currency
            </span>
          </div>
          <div className="ph-cfg-row">
            <span className="ph-cfg-sub" style={{ marginTop: 0 }}>🧪 pilot (none = everyone):</span>
            {visibleReps.map(r => (
              <button key={r}
                className={`ph-day-btn ph-team-btn ${(mp.pilot_reps || []).includes(r) ? 'on' : ''}`}
                onClick={() => updMp({
                  pilot_reps: (mp.pilot_reps || []).includes(r)
                    ? (mp.pilot_reps || []).filter(x => x !== r)
                    : [...(mp.pilot_reps || []), r],
                })}>{r.split(' ')[0]}</button>
            ))}
          </div>
          {mp.catalog.map((it, i) => (
            <div key={it.key} className="ph-cfg-block">
              <div className="ph-cfg-row">
                <label className="ph-cfg-toggle">
                  <input type="checkbox" checked={it.enabled}
                    onChange={e => updItem(i, { enabled: e.target.checked })} />
                  <input className="ph-cfg-name" placeholder="Item — e.g. 🏠 Work-From-Home Day" value={it.label}
                    onChange={e => updItem(i, { label: e.target.value })} />
                </label>
                <label title="What reps type in Slack: /coins redeem <code>. Two items the same team can see can't share one.">
                  /coins redeem <input className="ph-cfg-num" style={{ width: 90 }} placeholder="wfh"
                    value={it.code || ''}
                    onChange={e => updItem(i, { code: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })} />
                </label>
                <span className="ph-cfg-sub" style={{ marginTop: 0 }}>id {it.key}</span>
                <button className="ph-icon-btn" onClick={() => updMp({ catalog: mp.catalog.filter((_, j) => j !== i) })}><FiTrash2 /></button>
              </div>
              <div className="ph-cfg-row">
                <label>🪙 <input type="number" min={1} className="ph-cfg-num" value={it.coins}
                  onChange={e => updItem(i, { coins: parseInt(e.target.value) || 0 })} /></label>
                {(() => {
                  // what this price COSTS in weeks of work, right beside the box
                  // you type it into — the AE lockout lasted a season because
                  // nothing here ever compared a price to an earn rate
                  const e = econ?.catalog.find(c => c.key === it.key);
                  if (!e || it.poolable) return null;
                  const over = e.weeks_median === null || e.weeks_median > (econ?.season_weeks ?? 13);
                  return (
                    <span className={`ph-cfg-sub${over ? ' ph-cfg-warn' : ''}`} style={{ marginTop: 0 }}>
                      {e.weeks_median === null
                        ? `⚠️ ${e.team} earns nothing right now`
                        : `≈ ${e.weeks_median} wks for a typical ${e.team}`}
                      {e.weeks_slowest !== null && (
                        <>, {e.weeks_slowest} for the slowest</>
                      )}
                      {over && ' — beyond a season'}
                    </span>
                  );
                })()}
                <label>stock/mo <input type="number" min={0} className="ph-cfg-num" value={it.stock_per_month}
                  onChange={e => updItem(i, { stock_per_month: parseInt(e.target.value) || 0 })} /></label>
                <label>per-rep/mo <input type="number" min={0} className="ph-cfg-num" value={it.per_rep_per_month}
                  onChange={e => updItem(i, { per_rep_per_month: parseInt(e.target.value) || 0 })} /></label>
                <label>gift $ <input type="number" min={0} className="ph-cfg-num" value={it.gift_card_amount}
                  onChange={e => updItem(i, { gift_card_amount: parseFloat(e.target.value) || 0 })} /></label>
              </div>
              <div className="ph-cfg-row">
                <label className="ph-cfg-toggle"><input type="checkbox" checked={it.requires_approval}
                  onChange={e => updItem(i, { requires_approval: e.target.checked })} />needs approval</label>
                <label className="ph-cfg-toggle"><input type="checkbox" checked={it.poolable}
                  onChange={e => updItem(i, { poolable: e.target.checked })} />team pool</label>
                <span className="ph-cfg-sub" style={{ marginTop: 0 }}>who:</span>
                {Object.keys(cfg.teams).map(t => (
                  <button key={t} className={`ph-day-btn ph-team-btn ${(it.teams || []).includes(t) ? 'on' : ''}`}
                    onClick={() => updItem(i, {
                      teams: (it.teams || []).includes(t)
                        ? (it.teams || []).filter(x => x !== t)
                        : [...(it.teams || []), t],
                    })}>{t}</button>
                ))}
                <span className="ph-cfg-sub" style={{ marginTop: 0 }}>{(it.teams || []).length ? '' : '(everyone)'}</span>
              </div>
              <div className="ph-cfg-row">
                <span className="ph-cfg-sub" style={{ marginTop: 0 }}>🗓️ days:</span>
                {DAY_LABELS.slice(0, 5).map((d, di) => (
                  <button key={d} className={`ph-day-btn ${(it.eligible_days || []).includes(di) ? 'on' : ''}`}
                    onClick={() => updItem(i, {
                      eligible_days: (it.eligible_days || []).includes(di)
                        ? (it.eligible_days || []).filter(x => x !== di)
                        : [...(it.eligible_days || []), di],
                    })}>{d}</button>
                ))}
                <label className="ph-cfg-toggle">
                  <input type="checkbox" checked={!!it.blackout_power_hours}
                    onChange={e => updItem(i, { blackout_power_hours: e.target.checked })} />
                  ⛔ never on a power hour day
                </label>
                <span className="ph-cfg-sub" style={{ marginTop: 0 }}>
                  {it.blackout_power_hours
                    ? 'a day is required at redeem time'
                    : (it.eligible_days || []).length
                      ? 'checked only when the rep names a day'
                      : 'any weekday, day optional'}
                </span>
              </div>
            </div>
          ))}
          <button className="ph-mini-cta" onClick={() => {
            const label = '✨ New reward';
            updMp({
              catalog: [...mp.catalog, {
                key: slugKey(label), code: '', label, coins: 5000, teams: [], requires_approval: true,
                poolable: false, gift_card_amount: 0, stock_per_month: 4, per_rep_per_month: 1, enabled: false,
              }],
            });
          }}><FiPlus /> Add item</button>
        </div>

        {/* ---- Economy health ---- */}
        <div className="ph-cfg-card">
          <h3>📊 Economy <span className="ph-cfg-sub">what reps actually earn, measured over the last {econ?.weeks_sampled ?? 8} complete weeks (the week in progress is excluded, and a rep who booked nothing counts as a zero). Prices should be a fraction of a team's seasonal earn — re-check them each season.</span></h3>
          {!econ && <div className="ph-admin-hint">Loading the economy…</div>}
          {econ && (
            <>
              <div className="ph-cfg-row">
                <span className="ph-cfg-sub" style={{ marginTop: 0 }}>
                  <b>{econ.season}</b> · {econ.weeks_left} of {econ.season_weeks} weeks left
                </span>
              </div>
              {Object.entries(econ.teams).map(([team, t]) => (
                <div key={team} className="ph-cfg-block">
                  <div className="ph-cfg-row">
                    <span className="ph-cfg-name"><b>{team}</b> · {t.reps} reps</span>
                    <span className="ph-cfg-sub" style={{ marginTop: 0 }}>
                      typical rep <b>{t.median_coins_week.toLocaleString()}</b> 🪙/wk
                      {' '}(≈ {Math.round(t.median_coins_week * econ.season_weeks).toLocaleString()} a season)
                      {' · '}slowest is {t.slowest_rep || '—'} at{' '}
                      <b>{t.slowest_coins_week.toLocaleString()}</b> 🪙/wk
                    </span>
                  </div>
                </div>
              ))}

              <div className="ph-cfg-block">
                <div className="ph-cfg-row">
                  <span className="ph-cfg-sub" style={{ marginTop: 0 }}>
                    minted this season <b>{econ.minted_season.toLocaleString()}</b> 🪙
                    {' · '}redeemed <b>{econ.redeemed_season.toLocaleString()}</b>
                    {' · '}still spendable <b>{econ.overhang.toLocaleString()}</b>
                  </span>
                </div>
                {econ.minted_season > 0 && econ.redeemed_season === 0 && (
                  <div className="ph-cfg-sub">
                    Nothing has been redeemed yet — either the Vault is still closed, or the
                    catalog isn't worth what it costs.
                  </div>
                )}
                {econ.by_item.length > 0 && (
                  <div className="ph-cfg-sub">
                    {econ.by_item.map(b => `${b.label || b.key} ×${b.count}`).join(' · ')}
                  </div>
                )}
              </div>

              {econ.catalog.filter(c => !c.poolable).map(c => {
                const over = c.weeks_median === null || c.weeks_median > econ.season_weeks;
                const slowOver = c.weeks_slowest === null || c.weeks_slowest > econ.season_weeks;
                return (
                  <div key={c.key} className="ph-cfg-row">
                    <span className="ph-cfg-name">
                      {c.label} <span className="ph-cfg-sub">{c.team}{c.enabled ? '' : ' · off'}</span>
                    </span>
                    <span className={`ph-cfg-sub${over ? ' ph-cfg-warn' : ''}`} style={{ marginTop: 0 }}>
                      {c.coins.toLocaleString()} 🪙 ·{' '}
                      {c.weeks_median === null ? 'unreachable' : `${c.weeks_median} wks typical`}
                      {c.pct_of_season !== null && ` (${Math.round(c.pct_of_season * 100)}% of a season)`}
                      {slowOver && !over && ' · ⚠️ out of reach for the slowest rep all season'}
                      {over && ' · ⚠️ beyond a season even for a typical rep'}
                    </span>
                  </div>
                );
              })}

              {econ.unmapped_reps.length > 0 && (
                <div className="ph-cfg-sub">
                  ⚠️ No email on file for {econ.unmapped_reps.join(', ')} — their spend can't be
                  matched, so their balance reads high and they're left out of the totals above.
                </div>
              )}
            </>
          )}
        </div>

        {/* ---- Team milestones ---- */}
        <div className="ph-cfg-card">
          <h3>🏔️ Team Reward Ladder <span className="ph-cfg-sub">the team prize — flags on the TV mountain · meetings per
            <select value={cfg.milestones.period}
              onChange={e => upd({ milestones: { ...cfg.milestones, period: e.target.value } })}>
              <option value="day">day</option><option value="week">week</option><option value="month">month</option>
            </select></span></h3>
          {cfg.milestones.items.map((m, i) => (
            <div key={i} className="ph-cfg-row">
              <input type="number" min={1} className="ph-cfg-num" value={m.meetings}
                onChange={e => upd({ milestones: { ...cfg.milestones, items: cfg.milestones.items.map((x, j) => j === i ? { ...x, meetings: parseInt(e.target.value) || 0 } : x) } })} />
              <input placeholder="Reward — e.g. 🏌️ Golf outing" value={m.reward}
                onChange={e => upd({ milestones: { ...cfg.milestones, items: cfg.milestones.items.map((x, j) => j === i ? { ...x, reward: e.target.value } : x) } })} />
              <button className="ph-icon-btn" onClick={() => upd({ milestones: { ...cfg.milestones, items: cfg.milestones.items.filter((_, j) => j !== i) } })}><FiTrash2 /></button>
            </div>
          ))}
          <button className="ph-mini-cta" onClick={() => upd({
            milestones: { ...cfg.milestones, items: [...cfg.milestones.items, { meetings: 25, reward: '' }] },
          })}><FiPlus /> Add milestone</button>
        </div>

        {/* ---- Teams ---- */}
        <div className="ph-cfg-card">
          <h3>⚔️ Team Battle Rosters <span className="ph-cfg-sub">any teams you like — Nooks has no group API, assign reps once here</span></h3>
          <div className="ph-cfg-row">
            <input placeholder="New team name… (e.g. Wolfpack)" value={newTeam}
              onChange={e => setNewTeam(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addTeam(); }} />
            <button className="ph-mini-cta" onClick={addTeam}><FiPlus /> Team</button>
          </div>
          <div className="ph-cfg-row">
            {Object.keys(cfg.teams).map(t => (
              <span key={t} className="ph-cfg-teamchip">
                {t} ({cfg.teams[t].length})
                <button onClick={() => removeTeam(t)} title="Remove team">×</button>
              </span>
            ))}
          </div>
          {visibleReps.map(rep => (
            <div key={rep} className="ph-cfg-row ph-cfg-roster-row">
              <span className="ph-cfg-rep">{rep}</span>
              {['', ...Object.keys(cfg.teams)].map(t => (
                <button key={t || 'none'}
                  className={`ph-day-btn ph-team-btn ${teamOf(rep) === t ? 'on' : ''}`}
                  onClick={() => setTeam(rep, t)}>{t || '—'}</button>
              ))}
              <button className="ph-day-btn ph-team-btn" title="Hide from all boards"
                onClick={() => upd({ excluded_reps: [...cfg.excluded_reps, rep] })}>🚫</button>
            </div>
          ))}
          <div className="ph-cfg-row">
            <label>⚔️ battle prize <input placeholder="e.g. losing team buys lunch"
              value={cfg.team_battle_prize || ''}
              onChange={e => upd({ team_battle_prize: e.target.value })} /></label>
          </div>
          {cfg.excluded_reps.length > 0 && (
            <div className="ph-cfg-row">
              <span className="ph-cfg-sub" style={{ marginTop: 0 }}>hidden:</span>
              {cfg.excluded_reps.map(r => (
                <span key={r} className="ph-cfg-teamchip">🚫 {r}
                  <button onClick={() => upd({ excluded_reps: cfg.excluded_reps.filter(x => x !== r) })} title="Unhide">×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ---- Streaks ---- */}
        <div className="ph-cfg-card">
          <h3>🔥 Streaks <span className="ph-cfg-sub">a day counts with ≥1 meeting or ≥25 dials · weekends never break a streak</span></h3>
          <div className="ph-cfg-row">
            <label className="ph-cfg-toggle">
              <input type="checkbox" checked={cfg.streaks.nudges}
                onChange={e => upd({ streaks: { ...cfg.streaks, nudges: e.target.checked } })} />
              Afternoon "streak at risk" nudge
            </label>
            <label>at <input type="number" min={9} max={16} value={cfg.streaks.nudge_hour}
              onChange={e => upd({ streaks: { ...cfg.streaks, nudge_hour: parseInt(e.target.value) || 15 } })} />:00</label>
          </div>
          <div className="ph-cfg-row">
            <label>reward every <input type="number" min={2} max={30} value={cfg.streaks.reward_days}
              onChange={e => upd({ streaks: { ...cfg.streaks, reward_days: parseInt(e.target.value) || 5 } })} /> days</label>
            <input placeholder="Streak reward — e.g. ☕ $10 coffee card" value={cfg.streaks.reward}
              onChange={e => upd({ streaks: { ...cfg.streaks, reward: e.target.value } })} />
          </div>
        </div>

        {/* ---- Prize payouts ---- */}
        <div className="ph-cfg-card">
          <h3>💸 Prize Payouts <span className="ph-cfg-sub">crowned Champion/Top Closer winners awaiting a gift-card send — you approve, the rep picks their own reward from the catalog. Nothing sends automatically.</span></h3>
          {!fulfillmentReady && (
            <div className="ph-admin-hint">⚠️ Tremendous isn't configured yet — set TREMENDOUS_API_KEY + TREMENDOUS_FUNDING_SOURCE_ID in .env to enable sending.</div>
          )}
          {payouts.length === 0 && <div className="ph-admin-hint">No payouts waiting. Winners crowned at 5 PM appear here.</div>}
          {payouts.map(p => {
            const sending = busyPayoutId === p.id;
            const otherBusy = busyPayoutId !== null && !sending;
            return (
              <div key={p.id} className={`ph-cfg-row ph-payout-row${sending ? ' ph-payout-sending' : ''}`}>
                <span className="ph-payout-meta">
                  {({ champion: '🏆', closer: '🎯', hustler: '🔥' } as Record<string, string>)[p.track] || '🏅'} <b>{p.winner_name}</b> · ${p.amount} · <span className="ph-cfg-sub">
                    {p.period_key} {['champion', 'closer', 'hustler'].includes(p.track) ? p.track : p.prize_label}</span>
                </span>
                <input type="email" placeholder="rep email" value={p.winner_email || ''} disabled={sending || otherBusy}
                  onChange={e => setPayoutEmail(p.id, e.target.value)} />
                <button className="ph-mini-cta" disabled={!fulfillmentReady || !p.winner_email || sending || otherBusy}
                  onClick={() => approvePayout(p.id, p.winner_email)}>
                  {sending ? <><span className="ph-btn-spinner" /> SENDING…</> : 'Approve & send'}
                </button>
                <button className="ph-icon-btn" title="Skip (fulfilled another way)" disabled={sending || otherBusy}
                  onClick={() => skipPayout(p.id)}><FiTrash2 /></button>
              </div>
            );
          })}
          {busyPayoutId !== null && busyKind === 'send' && (
            <div className="ph-admin-hint">Contacting Tremendous — this takes a few seconds. Don't close this page.</div>
          )}
          {payoutMsg && (
            <div className={payoutMsg.ok ? 'ph-cfg-row' : 'ph-join-error'} style={{ marginTop: 4 }}>
              {payoutMsg.ok ? <span className="ph-cfg-sub" style={{ marginTop: 0 }}>{payoutMsg.text}</span> : payoutMsg.text}
            </div>
          )}
        </div>

        {/* ---- Marketplace redemption queue (backup path) ---- */}
        <div className="ph-cfg-card">
          <h3>🧾 Pending Redemptions <span className="ph-cfg-sub">backup queue — the approver normally decides from the Slack DM buttons. Approving fulfills (and sends any gift-card dollars); denying returns the coins</span></h3>
          {redemptions.length === 0 && <div className="ph-admin-hint">No redemptions waiting. Reps redeem with /coins in Slack.</div>}
          {redemptions.map(r => {
            const busy = busyRedemptionId === r.id;
            const otherBusy = busyRedemptionId !== null && !busy;
            return (
              <div key={r.id} className={`ph-cfg-row ph-payout-row${busy ? ' ph-payout-sending' : ''}`}>
                <span className="ph-payout-meta">
                  {r.kind === 'pool_redeem' ? '🫙' : '🛍️'} <b>{r.rep_name}</b> · {r.item_label} · <span className="ph-cfg-sub">
                    {r.kind === 'pool_redeem' ? 'team pool' : `${(r.coins || 0).toLocaleString()} 🪙`}{r.gift_card_amount > 0 ? ` · $${r.gift_card_amount}` : ''}{r.requested_date ? ` · 🗓️ ${r.requested_date}` : ''} · {r.season_key}</span>
                </span>
                <button className="ph-mini-cta" disabled={busy || otherBusy}
                  onClick={() => decideRedemption(r.id, true)}>
                  {busy ? <><span className="ph-btn-spinner" /> WORKING…</> : 'Approve'}
                </button>
                <button className="ph-icon-btn" title="Deny — coins go back to the rep" disabled={busy || otherBusy}
                  onClick={() => decideRedemption(r.id, false)}><FiTrash2 /></button>
              </div>
            );
          })}
          {redemptionMsg && (
            <div className={redemptionMsg.ok ? 'ph-cfg-row' : 'ph-join-error'} style={{ marginTop: 4 }}>
              {redemptionMsg.ok ? <span className="ph-cfg-sub" style={{ marginTop: 0 }}>{redemptionMsg.text}</span> : redemptionMsg.text}
            </div>
          )}
        </div>

        {/* ---- One-off bonus coin award ---- */}
        <div className="ph-cfg-card">
          <h3>🎰 Award Bonus Coins <span className="ph-cfg-sub">a one-off coin drop for anyone — counts toward their leaderboard &amp; prize standings, fires a JACKPOT in Slack + a coins-falling animation on the TV</span></h3>
          <div className="ph-cfg-row">
            <input list="ph-award-reps" placeholder="Who? (name)" value={awardName}
              onChange={e => setAwardName(e.target.value)} />
            <datalist id="ph-award-reps">
              {visibleReps.map(r => <option key={r} value={r} />)}
            </datalist>
            <input type="number" min={1} className="ph-cfg-num" placeholder="🪙 coins" value={awardCoins}
              onChange={e => setAwardCoins(e.target.value)} />
          </div>
          <div className="ph-cfg-row">
            <input placeholder="Reason (optional) — e.g. clutch save on the Acme deal" value={awardNote}
              onChange={e => setAwardNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') awardBonus(); }} />
            <button className="ph-mini-cta" onClick={awardBonus} disabled={awarding}>
              <FiZap style={{ verticalAlign: '-2px' }} /> {awarding ? 'AWARDING…' : 'AWARD'}
            </button>
          </div>
          {awardMsg && (
            <div className={awardMsg.ok ? 'ph-cfg-row' : 'ph-join-error'} style={{ marginTop: 4 }}>
              {awardMsg.ok ? <span className="ph-cfg-sub" style={{ marginTop: 0 }}>{awardMsg.text}</span> : awardMsg.text}
            </div>
          )}
        </div>

        {/* ---- Manual gift card (real money) ---- */}
        <div className="ph-cfg-card">
          <h3>💳 Issue Gift Card <span className="ph-cfg-sub">a one-off REAL gift card for anyone, sent immediately — the rep gets a reward link by email and picks their own card. Fires a GIFT CARD JACKPOT in Slack + on the TV; logged in the payouts history</span></h3>
          {!fulfillmentReady && (
            <div className="ph-admin-hint">⚠️ Tremendous isn't configured yet — set TREMENDOUS_API_KEY + TREMENDOUS_FUNDING_SOURCE_ID in .env to enable sending.</div>
          )}
          <div className="ph-cfg-row">
            <input list="ph-award-reps" placeholder="Who? (name)" value={giftName}
              onChange={e => setGiftName(e.target.value)} />
            <input type="number" min={1} max={1000} className="ph-cfg-num" placeholder="$ amount" value={giftAmount}
              onChange={e => setGiftAmount(e.target.value)} />
            <input type="email" placeholder="email (auto if known)" value={giftEmail}
              onChange={e => setGiftEmail(e.target.value)} />
          </div>
          <div className="ph-cfg-row">
            <input placeholder="Reason (optional) — e.g. biggest deal of the quarter" value={giftNote}
              onChange={e => setGiftNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') issueGiftCard(); }} />
            <button className="ph-mini-cta" onClick={issueGiftCard} disabled={gifting || !fulfillmentReady}>
              <FiGift style={{ verticalAlign: '-2px' }} /> {gifting ? 'SENDING…' : 'SEND GIFT CARD'}
            </button>
          </div>
          {giftMsg && (
            <div className={giftMsg.ok ? 'ph-cfg-row' : 'ph-join-error'} style={{ marginTop: 4 }}>
              {giftMsg.ok ? <span className="ph-cfg-sub" style={{ marginTop: 0 }}>{giftMsg.text}</span> : giftMsg.text}
            </div>
          )}
        </div>

        {/* ---- Slack ---- */}
        <div className="ph-cfg-card">
          <h3>📣 Slack Hype <span className="ph-cfg-sub">per channel: ⚡ power-hour reminder/go-live/summary · 🚀 bonus-hour pings · 🏆 daily 5pm / Friday weekly / month-end winners · 🎉 real-time meeting celebrations (2nd+ meeting today, new streaks; @mentions the rep)</span></h3>
          <div className="ph-cfg-row">
            <label className="ph-cfg-toggle">
              <input type="checkbox" checked={cfg.slack.announce}
                onChange={e => upd({ slack: { ...cfg.slack, announce: e.target.checked } })} />
              Announcements on
            </label>
            <label>remind (min before) <input type="number" min={5} max={120} value={cfg.slack.remind_minutes}
              onChange={e => upd({ slack: { ...cfg.slack, remind_minutes: parseInt(e.target.value) || 30 } })} /></label>
          </div>
          {(cfg.slack.channels || []).map((ch, i) => (
            <div key={i} className="ph-cfg-row">
              <select value={ch.channel}
                onChange={e => upd({ slack: { ...cfg.slack, channels: cfg.slack.channels.map((x, j) => j === i ? { ...x, channel: e.target.value } : x) } })}>
                <option value="">— pick a channel —</option>
                {slackChannels.map(c => (
                  <option key={c.id} value={c.id}>#{c.name}</option>
                ))}
                {ch.channel && !slackChannels.some(c => c.id === ch.channel) && (
                  <option value={ch.channel}>{ch.channel}</option>
                )}
              </select>
              <label className="ph-cfg-toggle"><input type="checkbox" checked={ch.power_hours}
                onChange={e => upd({ slack: { ...cfg.slack, channels: cfg.slack.channels.map((x, j) => j === i ? { ...x, power_hours: e.target.checked } : x) } })} />⚡ power hours</label>
              <label className="ph-cfg-toggle"><input type="checkbox" checked={ch.boosts}
                onChange={e => upd({ slack: { ...cfg.slack, channels: cfg.slack.channels.map((x, j) => j === i ? { ...x, boosts: e.target.checked } : x) } })} />🚀 boosts</label>
              <label className="ph-cfg-toggle"><input type="checkbox" checked={ch.winners ?? ch.power_hours}
                onChange={e => upd({ slack: { ...cfg.slack, channels: cfg.slack.channels.map((x, j) => j === i ? { ...x, winners: e.target.checked } : x) } })} />🏆 winners</label>
              <label className="ph-cfg-toggle"><input type="checkbox" checked={ch.celebrations ?? false}
                onChange={e => upd({ slack: { ...cfg.slack, channels: cfg.slack.channels.map((x, j) => j === i ? { ...x, celebrations: e.target.checked } : x) } })} />🎉 celebrations</label>
              <button className="ph-icon-btn" onClick={() => upd({ slack: { ...cfg.slack, channels: cfg.slack.channels.filter((_, j) => j !== i) } })}><FiTrash2 /></button>
            </div>
          ))}
          <button className="ph-mini-cta" onClick={() => upd({
            slack: { ...cfg.slack, channels: [...(cfg.slack.channels || []), { channel: '', power_hours: true, boosts: false }] },
          })}><FiPlus /> Add channel</button>
        </div>
      </div>
    </div>
  );
};

export default PowerHourConfig;
